<template lang="pug">
v-container(fluid)
  v-row
    v-col(cols="12" md="6")
      v-card.mb-6
        v-card-title.headline
          | Generador de URLs para Campañas
        v-card-subtitle
          | Creá links para tus campañas de marketing digital

        v-card-text
          v-form(ref="form" v-model="valid" lazy-validation)
            v-text-field(
              v-model="campaignURL.name"
              label="Nombre de la campaña"
              :rules="[v => !!v || 'El nombre es obligatorio']"
              outlined
              dense
              required
            )

            v-text-field(
              v-model="campaignURL.description"
              label="Descripción (opcional)"
              outlined
              dense
            )

            v-combobox(
              v-model="campaignURL.baseURL"
              :items="baseUrlOptions"
              label="URL base"
              outlined
              dense
              :rules="[v => !!v || 'La URL base es obligatoria', v => !v || v.startsWith('http') || 'La URL debe comenzar con http:// o https://']"
              required
              hint="Podés ingresar tu propia URL personalizada"
              persistent-hint
            )

            .subtitle-1.font-weight-medium.mb-2 Parámetros UTM
            
            v-row
              v-col(cols="12" md="6")
                v-autocomplete(
                  v-model="campaignURL.utmSource"
                  :items="sourceOptions"
                  label="Fuente (utm_source)"
                  outlined
                  dense
                  :rules="[v => !!v || 'La fuente es obligatoria']"
                  required
                  hint="¿De dónde viene el tráfico? (ej: google, facebook)"
                  persistent-hint
                )
              v-col(cols="12" md="6")
                v-autocomplete(
                  v-model="campaignURL.utmMedium"
                  :items="mediumOptions"
                  label="Medio (utm_medium)"
                  outlined
                  dense
                  :rules="[v => !!v || 'El medio es obligatorio']"
                  required
                  hint="¿Qué canal usás? (ej: cpc, banner, email)"
                  persistent-hint
                )
            
            v-row
              v-col(cols="12" md="6")
                v-text-field(
                  v-model="campaignURL.utmCampaign"
                  label="Campaña (utm_campaign)"
                  outlined
                  dense
                  :rules="[v => !!v || 'El nombre de campaña es obligatorio']"
                  required
                  hint="Identifica tu campaña específica (ej: promo_verano)"
                  persistent-hint
                )
              v-col(cols="12" md="6")
                v-text-field(
                  v-model="campaignURL.utmContent"
                  label="Contenido (utm_content)"
                  outlined
                  dense
                  hint="Identifica qué versión del anuncio generó el clic"
                  persistent-hint
                )
            
            v-text-field(
              v-model="campaignURL.utmTerm"
              label="Términos (utm_term)"
              outlined
              dense
              hint="Para identificar palabras clave pagadas"
              persistent-hint
            )

            v-menu(
              v-model="dateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            )
              template(v-slot:activator="{ on, attrs }")
                v-text-field(
                  :value="formattedExpiration"
                  label="Fecha de expiración (opcional)"
                  readonly
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on"
                  clearable
                  @click:clear="clearExpirationDate"
                )
              v-date-picker(
                v-model="expirationDate"
                @input="dateMenu = false"
                locale="es-AR"
                :min="minDate"
              )

            v-chip-group(
              v-model="selectedTags"
              column
              multiple
            )
              v-chip(
                v-for="tag in availableTags"
                :key="tag"
                filter
                outlined
              )
                | {{ tag }}
        
        v-card-actions
          v-spacer
          v-btn(color="error" text @click="resetForm")
            | Cancelar
          v-btn(
            color="primary"
            :loading="loading"
            :disabled="!valid"
            @click="saveCampaignURL"
          )
            v-icon(left) mdi-content-save
            | Guardar
    
    v-col(cols="12" md="6")
      v-card.mb-6
        v-card-title
          | Vista previa de la URL
        v-card-text
          v-alert(
            color="info"
            outlined
            dense
            text
            class="url-preview"
            :style="{ wordBreak: 'break-all' }"
          )
            | {{ generatedURL }}
          
          v-btn(
            block 
            color="primary" 
            :disabled="!generatedURL" 
            @click="copyToClipboard"
            class="mt-4"
          )
            v-icon(left) mdi-content-copy
            | Copiar al portapapeles

          v-alert(
            v-model="showCopyAlert"
            dismissible
            color="success"
            text
            class="mt-4"
          )
            | URL copiada al portapapeles
      
      v-card
        v-card-title.d-flex.justify-space-between.align-center
          | URLs guardadas
          v-btn(icon color="primary" @click="fetchCampaignURLs" :loading="loading")
            v-icon mdi-refresh
        
        v-data-table(
          :headers="[ { text: 'Nombre', value: 'name', sortable: true }, { text: 'Fuente', value: 'utmSource', sortable: true }, { text: 'Campaña', value: 'utmCampaign', sortable: true }, { text: 'Expira', value: 'expirationDate', sortable: true }, { text: 'Acciones', value: 'actions', sortable: false } ]"
          :items="campaignURLs"
          :loading="loading"
          :items-per-page="5"
          class="elevation-1"
        )
          template(v-slot:item.expirationDate="{ item }")
            | {{ item.expirationDate ? formatDate(item.expirationDate) : 'Sin expiración' }}
          
          template(v-slot:item.actions="{ item }")
            v-btn(icon small @click="editItem(item)")
              v-icon(small) mdi-pencil
            v-btn(icon small @click="copyItemURL(item)")
              v-icon(small) mdi-content-copy
            v-btn(icon small color="error" @click="deleteItem(item)")
              v-icon(small) mdi-delete
          
          template(v-slot:no-data)
            v-alert(
              :value="true"
              color="info"
              icon="mdi-information"
              outlined
            )
              | No hay URLs guardadas aún.
  
  v-dialog(v-model="deleteDialog" max-width="500px")
    v-card
      v-card-title ¿Estás seguro?
      v-card-text Esta acción no se puede deshacer.
      v-card-actions
        v-spacer
        v-btn(color="blue darken-1" text @click="closeDeleteDialog") Cancelar
        v-btn(color="error" text @click="confirmDelete") Eliminar
        v-spacer
</template>

<script>
import { mapState } from 'vuex';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export default {
  name: 'CampaignURLGenerator',
  
  data() {
    return {
      valid: false,
      search: '',
      campaignURL: {
        name: '',
        description: '',
        baseURL: '',
        utmSource: '',
        utmMedium: '',
        utmCampaign: '',
        utmContent: '',
        utmTerm: '',
        expirationDate: null,
        tags: [],
        active: true
      },
      selectedTags: [],
      dateMenu: false,
      expirationDate: null,
      showCopyAlert: false,
      headers: [
        { text: 'Nombre', value: 'name' },
        { text: 'Fuente', value: 'utmSource' },
        { text: 'Medio', value: 'utmMedium' },
        { text: 'Campaña', value: 'utmCampaign' },
        { text: 'Estado', value: 'active' },
        { text: 'Creada', value: 'createdAt' },
        { text: 'Acciones', value: 'actions', sortable: false }
      ],
      sourceOptions: [
        'google', 'facebook', 'instagram', 'tiktok', 'youtube',
        'email', 'newsletter', 'whatsapp', 'sms', 'direct', 'referral'
      ],
      mediumOptions: [
        'cpc', 'display', 'social', 'email', 'affiliate', 'referral',
        'organic', 'paid-social', 'banner', 'retargeting', 'push'
      ],
      availableTags: [
        'verano', 'invierno', 'promoción', 'descuento', 'evento',
        'delivery', 'takeaway', 'reservas', 'finde', 'happy-hour'
      ],
      baseUrlOptions: [],
      editMode: false,
      editId: null,
      deleteDialog: false,
      itemToDelete: null
    };
  },
  
  computed: {
    ...mapState('marketing', ['campaignURLs', 'loading', 'error']),
    ...mapState('restaurant', ['restaurant']),
    
    formattedExpiration() {
      if (!this.expirationDate) return ''
      // Usar el mismo criterio que ExpensesView para strings YYYY-MM-DD
      if (typeof this.expirationDate === 'string' && this.expirationDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [y, m, d] = this.expirationDate.split('-').map(Number)
        const localDate = new Date(y, m - 1, d)
        return format(localDate, 'dd/MM/yyyy', { locale: es })
      }
      return format(new Date(this.expirationDate), 'dd/MM/yyyy', { locale: es })
    },
    
    minDate() {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      const d = String(now.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    },
    
    generatedURL() {
      if (!this.campaignURL.baseURL) return '';
      
      let url = this.campaignURL.baseURL;
      const params = new URLSearchParams();
      
      if (this.campaignURL.utmSource) {
        params.append('utm_source', this.campaignURL.utmSource);
      }
      
      if (this.campaignURL.utmMedium) {
        params.append('utm_medium', this.campaignURL.utmMedium);
      }
      
      if (this.campaignURL.utmCampaign) {
        params.append('utm_campaign', this.campaignURL.utmCampaign);
      }
      
      if (this.campaignURL.utmContent) {
        params.append('utm_content', this.campaignURL.utmContent);
      }
      
      if (this.campaignURL.utmTerm) {
        params.append('utm_term', this.campaignURL.utmTerm);
      }
      
      const paramString = params.toString();
      if (paramString) {
        url += (url.includes('?') ? '&' : '?') + paramString;
      }
      
      return url;
    }
  },
  
  watch: {
    expirationDate(val) {
      // Guardar como string local YYYY-MM-DD para evitar desfases
      if (typeof val === 'string' && val.match(/^\d{4}-\d{2}-\d{2}$/)) {
        this.campaignURL.expirationDate = val
      } else if (val instanceof Date) {
        const y = val.getFullYear()
        const m = String(val.getMonth() + 1).padStart(2, '0')
        const d = String(val.getDate()).padStart(2, '0')
        this.campaignURL.expirationDate = `${y}-${m}-${d}`
      } else {
        this.campaignURL.expirationDate = val
      }
    },
    
    selectedTags(val) {
      this.campaignURL.tags = val.map(index => this.availableTags[index]);
    }
  },
  
  created() {
    this.fetchCampaignURLs();
    this.buildBaseUrlOptions();
  },
  
  methods: {
    async fetchCampaignURLs() {
      try {
        await this.$store.dispatch('marketing/fetchCampaignURLs');
      } catch (error) {
        console.error('Error al cargar URLs de campañas:', error);
      }
    },
    
    buildBaseUrlOptions() {
      const options = [];
      
      if (this.$store.state.auth.user.restaurant.urlSlug) {
        // URL principal del restaurante
        if (this.$store.state.auth.user.restaurant.urlSlug) {
          options.push(`https://restonline.com.ar/r/${this.$store.state.auth.user.restaurant.urlSlug}`);
        }
        
        // URL de menú
        if (this.$store.state.auth.user.restaurant.urlSlug) {
          options.push(`https://restonline.com.ar/r/${this.$store.state.auth.user.restaurant.urlSlug}#menu`);
        }
        
        // URL de reservas
        if (this.$store.state.auth.user.restaurant.urlSlug) {
          options.push(`https://restonline.com.ar/r/${this.$store.state.auth.user.restaurant.urlSlug}/reservas`);
        }
      }
      
      // URL principal de Restonline
      options.push('https://restonline.com.ar');
      
      this.baseUrlOptions = options;
    },
    
    async saveCampaignURL() {
      if (!this.$refs.form.validate()) return;
      
      try {
        const campaignData = { ...this.campaignURL };
        
        // Mantener YYYY-MM-DD tal cual (sin toISOString)
        if (this.expirationDate) {
          campaignData.expirationDate = this.expirationDate;
        }
        
        if (this.editMode) {
          await this.$store.dispatch('marketing/updateCampaignURL', {
            urlId: this.editId,
            campaignData
          });
          this.$toast.success('URL de campaña actualizada correctamente');
        } else {
          await this.$store.dispatch('marketing/createCampaignURL', campaignData);
          this.$toast.success('URL de campaña creada correctamente');
        }
        
        this.resetForm();
      } catch (error) {
        console.error('Error al guardar la URL de campaña:', error);
        this.$toast.error('Error al guardar la URL de campaña');
      }
    },
    
    resetForm() {
      this.$refs.form.reset();
      this.selectedTags = [];
      this.expirationDate = null;
      this.editMode = false;
      this.editId = null;
      this.campaignURL = {
        name: '',
        description: '',
        baseURL: '',
        utmSource: '',
        utmMedium: '',
        utmCampaign: '',
        utmContent: '',
        utmTerm: '',
        expirationDate: null,
        tags: [],
        active: true
      };
    },
    
    copyToClipboard() {
      if (!this.generatedURL) return;
      
      navigator.clipboard.writeText(this.generatedURL)
        .then(() => {
          this.showCopyAlert = true;
          setTimeout(() => {
            this.showCopyAlert = false;
          }, 3000);
        })
        .catch(err => {
          console.error('Error al copiar URL:', err);
          this.$toast.error('Error al copiar la URL al portapapeles');
        });
    },
    
    formatDate(dateString) {
      try {
        // Manejo robusto para fechas de solo día (YYYY-MM-DD) evitando desfase por zona horaria
        if (!dateString) return 'Sin expiración';

        let iso = '';
        if (dateString instanceof Date) {
          iso = dateString.toISOString();
        } else if (typeof dateString === 'string') {
          iso = dateString;
        } else if (dateString && dateString.toISOString) {
          iso = dateString.toISOString();
        } else {
          iso = String(dateString);
        }

        // Extraer la parte YYYY-MM-DD si viene en ISO (o ya es YYYY-MM-DD)
        const match = iso.match(/^\d{4}-\d{2}-\d{2}/);
        if (match) {
          const [y, m, d] = match[0].split('-').map(Number);
          const localDate = new Date(y, m - 1, d); // Fecha local sin aplicar UTC
          return format(localDate, 'dd/MM/yyyy', { locale: es });
        }

        // Fallback seguro
        return format(new Date(iso), 'dd/MM/yyyy', { locale: es });
      } catch (e) {
        return typeof dateString === 'string' ? dateString : '';
      }
    },
    
    editItem(item) {
      this.editMode = true;
      this.editId = item._id;
      
      // Convertir las etiquetas a índices para v-chip-group
      const tagIndices = [];
      if (item.tags && item.tags.length) {
        item.tags.forEach(tag => {
          const index = this.availableTags.indexOf(tag);
          if (index !== -1) {
            tagIndices.push(index);
          }
        });
      }
      
      this.selectedTags = tagIndices;
      
      // Copiar propiedades
      this.campaignURL = { ...item };
      
      // Manejar fecha de expiración (admite ISO o YYYY-MM-DD)
      if (item.expirationDate) {
        if (typeof item.expirationDate === 'string' && item.expirationDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
          this.expirationDate = item.expirationDate
        } else {
          this.expirationDate = new Date(item.expirationDate).toISOString().substr(0, 10)
        }
      } else {
        this.expirationDate = null;
      }
      
      // Desplazar a la parte superior del formulario
      this.$vuetify.goTo(0);
    },
    clearExpirationDate() {
      this.expirationDate = null
      this.campaignURL.expirationDate = null
    },
    
    copyItemURL(item) {
      let url = item.baseURL;
      const params = new URLSearchParams();
      
      if (item.utmSource) params.append('utm_source', item.utmSource);
      if (item.utmMedium) params.append('utm_medium', item.utmMedium);
      if (item.utmCampaign) params.append('utm_campaign', item.utmCampaign);
      if (item.utmContent) params.append('utm_content', item.utmContent);
      if (item.utmTerm) params.append('utm_term', item.utmTerm);
      
      const paramString = params.toString();
      if (paramString) {
        url += (url.includes('?') ? '&' : '?') + paramString;
      }
      
      navigator.clipboard.writeText(url)
        .then(() => {
          this.$toast.success('URL copiada al portapapeles');
        })
        .catch(err => {
          console.error('Error al copiar URL:', err);
          this.$toast.error('Error al copiar la URL');
        });
    },
    
    deleteItem(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },
    
    closeDeleteDialog() {
      this.deleteDialog = false;
      this.itemToDelete = null;
    },
    
    async confirmDelete() {
      if (!this.itemToDelete) return;
      
      try {
        await this.$store.dispatch('marketing/deleteCampaignURL', this.itemToDelete._id);
        this.$toast.success('URL de campaña eliminada correctamente');
        this.closeDeleteDialog();
      } catch (error) {
        console.error('Error al eliminar URL de campaña:', error);
        this.$toast.error('Error al eliminar la URL de campaña');
      }
    }
  }
};
</script>

<style scoped>
.url-preview {
  font-family: monospace;
  padding: 16px;
  font-size: 0.95rem;
}

@media (max-width: 600px) {
  .headline {
    font-size: 1.25rem !important;
  }
}
</style>
